/**
 * Skeniranje dokumentacije u predmetu.
 *  - ScanWizard: čarobnjak za korisnika (izvor → stranice i prilozi → predaja). Izvori: mrežni skener (eSCL, službenici),
 *    kamera telefona/tableta, fajlovi (sken iz NAPS2/USB skenera, PDF sa više stranica).
 *  - ScannedAttachments: referent raspoređuje predate priloge (tip, naziv, datum, voćnjak, KO/KČ) ili ih dijeli.
 */
import {
  ActionIcon, Alert, Badge, Button, Card, Divider, Group, Loader, Modal, MultiSelect, Paper, SegmentedControl, Select,
  SimpleGrid, Stack, Stepper, Text, Textarea, TextInput, ThemeIcon, Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft, IconArrowRight, IconCamera, IconCheck, IconFileUpload, IconFolderPlus, IconPrinter, IconRotateClockwise,
  IconScan, IconScissors, IconTrash, IconWifi, IconWifiOff, IconZoomIn,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { downloadFile } from "../../api/http";
import { http } from "../../api/http";
import { AuthImage, useAuthBlobUrl } from "../../components/AuthImage";
import { fmtDate, fmtDateTime } from "../../components/ui";
import { useT } from "../../i18n/script";
import { errMsg, fmtSize, useKoAndMunicipalities } from "./common";

interface Page {
  id: number; seq: number; attachment_no: number; mime_type: string; size_bytes: number; rotation: number; source: string;
  original_name?: string | null;
}
interface ScanDoc {
  id: number; reg_no: string; title: string; doc_type: string; doc_type_name: string; doc_date?: string | null; description?: string | null;
  metadata: { prilog?: number; stranica?: number; naziv_podnosioca?: string | null; rasporedio?: string; rasporedjeno?: string; skup?: string };
  ko_id?: number | null; ko_name?: string | null; parcel_numbers: string[]; created_at: string; size_bytes?: number;
  rasporedjen: boolean; orchard_ids: number[];
}
interface Batch {
  id: number; number: string; case_id: number; case_number?: string; status: string; title?: string | null; note?: string | null;
  created_by?: string; created_by_name?: string; created_at: string; submitted_at?: string | null; pages: Page[] | number;
  documents: ScanDoc[]; unassigned?: number;
}
interface Scanner { id: number; name: string; location?: string | null; opstina?: string | null; defaults: Record<string, unknown> }
interface ScannerStatus {
  online: boolean; error?: string; state?: string; model?: string; sources?: string[]; duplex?: boolean; resolutions?: number[]; colors?: string[];
}

const SOURCE_LABEL: Record<string, string> = { skener: "skener", kamera: "kamera", fajl: "fajl" };
const BATCH_STATUS: Record<string, { label: string; color: string }> = {
  u_toku: { label: "Skeniranje u toku", color: "gray" }, predat: { label: "Čeka raspoređivanje", color: "orange" },
  rasporedjen: { label: "Raspoređeno", color: "green" }, odbacen: { label: "Odbačeno", color: "red" },
};

// ============================================================================== čarobnjak

function PageThumb({ bid, p, onRotate, onDelete, onMove, onZoom, attachments, onLeft, onRight }: {
  bid: number; p: Page; attachments: number[]; onRotate: () => void; onDelete: () => void; onMove: (a: number) => void;
  onZoom: () => void; onLeft?: () => void; onRight?: () => void;
}) {
  const { t } = useT();
  return (
    <Paper withBorder radius="md" p={4} style={{ position: "relative" }}>
      <div style={{ cursor: "zoom-in", background: "var(--mantine-color-gray-1)", borderRadius: 6, overflow: "hidden" }} onClick={onZoom}>
        <AuthImage path={`/api/v1/scan/batches/${bid}/pages/${p.id}/image?r=${p.rotation}`} alt={t("stranica")} h={170}
          style={{ objectFit: "contain" }} />
      </div>
      <Group justify="space-between" gap={2} mt={4} wrap="nowrap">
        <Badge size="xs" variant="light" color={p.source === "skener" ? "teal" : p.source === "kamera" ? "grape" : "gray"}>{t(SOURCE_LABEL[p.source] ?? p.source)}</Badge>
        <Group gap={0} wrap="nowrap">
          {onLeft && <ActionIcon size="sm" variant="subtle" onClick={onLeft} aria-label={t("Pomjeri lijevo")}><IconArrowLeft size={14} /></ActionIcon>}
          {onRight && <ActionIcon size="sm" variant="subtle" onClick={onRight} aria-label={t("Pomjeri desno")}><IconArrowRight size={14} /></ActionIcon>}
          <Tooltip label={t("Rotiraj 90°")}><ActionIcon size="sm" variant="subtle" onClick={onRotate}><IconRotateClockwise size={14} /></ActionIcon></Tooltip>
          <Tooltip label={t("Obriši stranicu")}><ActionIcon size="sm" variant="subtle" color="red" onClick={onDelete}><IconTrash size={14} /></ActionIcon></Tooltip>
        </Group>
      </Group>
      <Select size="xs" mt={4} value={String(p.attachment_no)} allowDeselect={false} comboboxProps={{ withinPortal: true }}
        data={[...attachments, Math.max(...attachments) + 1].map((a) => ({ value: String(a), label: `${t("Prilog")} ${a}` }))}
        onChange={(v) => v && onMove(Number(v))} aria-label={t("Prilog kojem stranica pripada")} />
    </Paper>
  );
}

function ZoomModal({ bid, page, onClose }: { bid: number; page: Page; onClose: () => void }) {
  const { t } = useT();
  const url = useAuthBlobUrl(`/api/v1/scan/batches/${bid}/pages/${page.id}/image?size=1600&r=${page.rotation}`);
  return (
    <Modal opened onClose={onClose} size="xl" title={`${t("Prilog")} ${page.attachment_no} — ${t("stranica")}`} zIndex={400}>
      {url ? <img src={url} alt="" style={{ width: "100%", display: "block" }} /> : <Loader />}
    </Modal>
  );
}

export function ScanWizard({ caseId, caseNumber = "", onClose }: { caseId: number; caseNumber?: string; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const mobile = useMediaQuery("(max-width: 48em)");
  const [step, setStep] = useState(0);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [current, setCurrent] = useState(1);
  const [titles, setTitles] = useState<Record<number, string>>({});
  const [note, setNote] = useState("");
  const [zoom, setZoom] = useState<Page | null>(null);
  const [done, setDone] = useState<{ batch: string; documents: { reg_no: string; prilog: number; pages: number }[] } | null>(null);
  const [scannerId, setScannerId] = useState<string | null>(null);
  const [scanSrc, setScanSrc] = useState("Platen");
  const [color, setColor] = useState("RGB24");
  const [res, setRes] = useState("300");
  const camRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const scanners = useQuery({ queryKey: ["scanners"], queryFn: () => http.get<Scanner[]>("/api/v1/scan/scanners") });
  const sstat = useQuery({
    queryKey: ["scanner-status", scannerId], enabled: !!scannerId, refetchInterval: 15000,
    queryFn: () => http.get<ScannerStatus>(`/api/v1/scan/scanners/${scannerId}/status`),
  });
  const created = useRef(false);

  useEffect(() => {  // skup priloga nastaje pri otvaranju čarobnjaka
    if (created.current) return;
    created.current = true;
    http.post<Batch>("/api/v1/scan/batches", { case_id: caseId, title: caseNumber ? `Dokumentacija za predmet ${caseNumber}` : null })
      .then(setBatch).catch((e) => { notifications.show({ color: "red", message: errMsg(e) }); onClose(); });
  }, [caseId, caseNumber, onClose]);
  useEffect(() => {
    const s = scanners.data?.[0];
    if (s && !scannerId) setScannerId(String(s.id));
  }, [scanners.data, scannerId]);

  const pages = (batch?.pages as Page[] | undefined) ?? [];
  const attachments = useMemo(() => {
    const s = new Set(pages.map((p) => p.attachment_no));
    s.add(current);
    return [...s].sort((a, b) => a - b);
  }, [pages, current]);

  const close = async () => {
    if (batch && batch.status === "u_toku" && !done && !pages.length) {
      try { await http.del(`/api/v1/scan/batches/${batch.id}`); } catch { /* nije bitno */ }
    }
    qc.invalidateQueries({ queryKey: ["scan-batches", caseId] });
    qc.invalidateQueries({ queryKey: ["case", String(caseId)] });
    onClose();
  };
  const run = async (label: string, fn: () => Promise<Batch & { added?: number }>) => {
    setBusy(label);
    try {
      const b = await fn();
      setBatch(b);
      if (b.added != null) notifications.show({ color: "green", message: `${t("Dodato stranica")}: ${b.added}` });
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(null); }
  };
  const upload = (files: FileList | null, source: "kamera" | "fajl") => {
    if (!files?.length || !batch) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    fd.append("source", source);
    fd.append("attachment_no", String(current));
    run(source === "kamera" ? "kamera" : "fajl", () => http.form(`/api/v1/scan/batches/${batch.id}/pages`, fd));
  };
  const scan = () => batch && scannerId && run("skener", () => http.post(`/api/v1/scan/batches/${batch.id}/scan`, {
    scanner_id: Number(scannerId), source: scanSrc, color, resolution: Number(res), attachment_no: current,
  }));
  const layout = (next: Page[]) => batch && run("layout", () => http.put(`/api/v1/scan/batches/${batch.id}/layout`,
    next.map((p) => ({ id: p.id, attachment_no: p.attachment_no, seq: p.seq, rotation: p.rotation }))));
  const movePage = (p: Page, att: number) => {
    const maxSeq = Math.max(0, ...pages.filter((x) => x.attachment_no === att).map((x) => x.seq));
    layout([{ ...p, attachment_no: att, seq: maxSeq + 1 }]);
  };
  const swap = (a: Page, b: Page) => layout([{ ...a, seq: b.seq }, { ...b, seq: a.seq }]);
  const del = (p: Page) => batch && run("del", async () => {
    await http.del(`/api/v1/scan/batches/${batch.id}/pages/${p.id}`);
    return http.get(`/api/v1/scan/batches/${batch.id}`);
  });
  const submit = async () => {
    if (!batch) return;
    setBusy("submit");
    try {
      const r = await http.post<{ batch: string; documents: { reg_no: string; prilog: number; pages: number }[] }>(
        `/api/v1/scan/batches/${batch.id}/submit`,
        { attachments: Object.entries(titles).map(([k, v]) => ({ attachment_no: Number(k), title: v })), note: note || null });
      setDone(r);
      qc.invalidateQueries({ queryKey: ["scan-batches", caseId] });
      qc.invalidateQueries({ queryKey: ["case", String(caseId)] });
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(null); }
  };

  const hasScanner = (scanners.data?.length ?? 0) > 0;
  const groups = attachments.map((a) => ({ no: a, pages: pages.filter((p) => p.attachment_no === a).sort((x, y) => x.seq - y.seq) }));
  const filled = groups.filter((g) => g.pages.length);

  const sourceBar = (
    <Paper withBorder p="sm" radius="md" style={{ position: "sticky", top: 0, zIndex: 5, background: "var(--mantine-color-body)" }}>
      <Group justify="space-between" gap="xs">
        <Group gap="xs">
          {hasScanner && (
            <Button leftSection={<IconScan size={16} />} loading={busy === "skener"} disabled={!scannerId || sstat.data?.online === false} onClick={scan}>
              {t(scanSrc === "Feeder" ? "Skeniraj iz uvlakača" : "Skeniraj stranicu")}</Button>
          )}
          <Button variant={hasScanner ? "light" : "filled"} leftSection={<IconCamera size={16} />} loading={busy === "kamera"} onClick={() => camRef.current?.click()}>
            {t("Fotografiši")}</Button>
          <Button variant="light" leftSection={<IconFileUpload size={16} />} loading={busy === "fajl"} onClick={() => fileRef.current?.click()}>
            {t("Dodaj fajlove")}</Button>
        </Group>
        <Group gap="xs">
          <Text size="sm">{t("Dodaje se u")}:</Text>
          <Select size="xs" w={130} value={String(current)} allowDeselect={false}
            data={attachments.map((a) => ({ value: String(a), label: `${t("Prilog")} ${a}` }))} onChange={(v) => v && setCurrent(Number(v))} />
          <Button size="xs" variant="default" leftSection={<IconFolderPlus size={14} />} onClick={() => setCurrent(Math.max(...attachments) + 1)}>
            {t("Novi prilog")}</Button>
        </Group>
      </Group>
      <input ref={camRef} type="file" accept="image/*" capture="environment" multiple hidden
        onChange={(e) => { upload(e.currentTarget.files, "kamera"); e.currentTarget.value = ""; }} />
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.webp,image/*,application/pdf" multiple hidden
        onChange={(e) => { upload(e.currentTarget.files, "fajl"); e.currentTarget.value = ""; }} />
    </Paper>
  );

  return (
    <Modal opened onClose={close} fullScreen={mobile} size="90%" title={<Text fw={700}>{t("Skeniranje dokumentacije")}{caseNumber ? ` — ${caseNumber}` : ""}{batch ? ` · ${batch.number}` : ""}</Text>}
      closeOnClickOutside={false}>
      {!batch ? <Loader /> : done ? (
        <Stack align="center" py="xl">
          <ThemeIcon size={64} radius="xl" color="green"><IconCheck size={36} /></ThemeIcon>
          <Text fw={700} size="lg">{t("Skup priloga je predat")} ({done.batch})</Text>
          <Text c="dimmed" ta="center" maw={520}>{t("Svaki prilog je upisan u DMS sa djelovodnim brojem i vezan za predmet. Referent će rasporediti priloge (vrsta dokumenta, voćnjak, čestice).")}</Text>
          <Stack gap={4}>{done.documents.map((d) => <Text key={d.reg_no} size="sm">{t("Prilog")} {d.prilog}: <b>{d.reg_no}</b> — {d.pages} {t("str.")}</Text>)}</Stack>
          <Button onClick={close}>{t("Zatvori")}</Button>
        </Stack>
      ) : (
        <Stack>
          <Stepper active={step} onStepClick={(s) => (s < 2 || pages.length) && setStep(s)} size="sm">
            <Stepper.Step label={t("Izvor")} description={t("skener, kamera, fajl")} />
            <Stepper.Step label={t("Stranice i prilozi")} description={`${pages.length} ${t("str.")}`} />
            <Stepper.Step label={t("Predaja")} description={`${filled.length} ${t("priloga")}`} />
          </Stepper>

          {step === 0 && (
            <Stack>
              <Text size="sm" c="dimmed">{t("Dokumentacija dobijena uz predmet skenira se u jedan skup priloga. Stranice grupišete u priloge (npr. Prilog 1 — rješenje RPG, Prilog 2 — posjedovni list), a referent kasnije određuje čemu svaki prilog pripada.")}</Text>
              <SimpleGrid cols={{ base: 1, md: 3 }}>
                <Card withBorder radius="lg" style={{ opacity: hasScanner ? 1 : 0.6 }}>
                  <Group gap="xs" mb="xs"><ThemeIcon color="teal" variant="light"><IconPrinter size={18} /></ThemeIcon><Text fw={700}>{t("Mrežni skener")}</Text></Group>
                  {!hasScanner ? (
                    <Text size="sm" c="dimmed">{t("Nema podešenog skenera. Administrator DMS-a dodaje mrežne skenere (eSCL / AirScan) u Administracija → DMS → Skeneri. Skener na USB-u: skenirajte u PDF (npr. besplatnim programom NAPS2) i izaberite „Fajlovi“.")}</Text>
                  ) : (
                    <Stack gap="xs">
                      <Select size="xs" label={t("Skener")} value={scannerId} onChange={setScannerId}
                        data={(scanners.data ?? []).map((s) => ({ value: String(s.id), label: `${s.name}${s.location ? ` — ${s.location}` : ""}` }))} />
                      {sstat.isLoading ? <Loader size="xs" /> : sstat.data && (
                        <Group gap={6}>
                          {sstat.data.online ? <IconWifi size={16} color="var(--mantine-color-green-6)" /> : <IconWifiOff size={16} color="var(--mantine-color-red-6)" />}
                          <Text size="xs" c={sstat.data.online ? undefined : "red"}>{sstat.data.online ? `${sstat.data.model ?? ""} · ${sstat.data.state}` : sstat.data.error}</Text>
                        </Group>
                      )}
                      <SegmentedControl size="xs" value={scanSrc} onChange={setScanSrc} data={[
                        { value: "Platen", label: t("Staklo") },
                        ...((sstat.data?.sources ?? ["Platen"]).includes("Feeder") ? [{ value: "Feeder", label: t("Uvlakač (više listova)") }] : [])]} />
                      <Group grow>
                        <Select size="xs" value={color} onChange={(v) => v && setColor(v)} data={[{ value: "RGB24", label: t("U boji") }, { value: "Grayscale8", label: t("Sivo") }]} />
                        <Select size="xs" value={res} onChange={(v) => v && setRes(v)} data={["150", "200", "300"].map((x) => ({ value: x, label: `${x} dpi` }))} />
                      </Group>
                      <Button size="xs" disabled={!sstat.data?.online} onClick={() => { setStep(1); scan(); }}>{t("Skeniraj")}</Button>
                    </Stack>
                  )}
                </Card>
                <Card withBorder radius="lg">
                  <Group gap="xs" mb="xs"><ThemeIcon color="grape" variant="light"><IconCamera size={18} /></ThemeIcon><Text fw={700}>{t("Kamera telefona / tableta")}</Text></Group>
                  <Text size="sm" c="dimmed" mb="sm">{t("Fotografišite svaku stranicu ravno, na tamnoj podlozi, uz dobro svjetlo. Na računaru se otvara izbor fajla.")}</Text>
                  <Button fullWidth variant="light" color="grape" onClick={() => { setStep(1); camRef.current?.click(); }}>{t("Fotografiši stranice")}</Button>
                </Card>
                <Card withBorder radius="lg">
                  <Group gap="xs" mb="xs"><ThemeIcon variant="light"><IconFileUpload size={18} /></ThemeIcon><Text fw={700}>{t("Fajlovi sa računara")}</Text></Group>
                  <Text size="sm" c="dimmed" mb="sm">{t("PDF (višestranični se dijeli na stranice), JPG, PNG, TIFF — npr. sken iz skenera na USB-u (NAPS2) ili iz skenera koji šalje na e-poštu / u mrežni folder.")}</Text>
                  <Button fullWidth variant="light" onClick={() => { setStep(1); fileRef.current?.click(); }}>{t("Izaberi fajlove")}</Button>
                </Card>
              </SimpleGrid>
            </Stack>
          )}

          {step === 1 && (
            <Stack>
              {sourceBar}
              {!pages.length && <Alert color="gray" variant="light">{t("Još nema stranica — skenirajte, fotografišite ili dodajte fajlove.")}</Alert>}
              {groups.map((g) => (
                <Card key={g.no} withBorder radius="md" style={{ borderColor: g.no === current ? "var(--mantine-color-teal-5)" : undefined }}>
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <Text fw={700}>{t("Prilog")} {g.no}</Text>
                      <Badge variant="light">{g.pages.length} {t("str.")}</Badge>
                      {g.no === current && <Badge color="teal" variant="dot">{t("ovdje se dodaju nove stranice")}</Badge>}
                    </Group>
                    <TextInput size="xs" w={mobile ? 170 : 320} placeholder={t("Naziv (npr. Rješenje RPG) — nije obavezno")}
                      value={titles[g.no] ?? ""} onChange={(e) => { const v = e.currentTarget.value; setTitles((x) => ({ ...x, [g.no]: v })); }} />
                  </Group>
                  {!g.pages.length ? <Text size="sm" c="dimmed">{t("Prazan prilog")}</Text> : (
                    <SimpleGrid cols={{ base: 2, sm: 4, lg: 6 }} spacing="xs">
                      {g.pages.map((p, i) => (
                        <PageThumb key={p.id} bid={batch.id} p={p} attachments={attachments}
                          onRotate={() => layout([{ ...p, rotation: (p.rotation + 90) % 360 }])}
                          onDelete={() => del(p)} onMove={(a) => movePage(p, a)} onZoom={() => setZoom(p)}
                          onLeft={i > 0 ? () => swap(p, g.pages[i - 1]) : undefined}
                          onRight={i < g.pages.length - 1 ? () => swap(p, g.pages[i + 1]) : undefined} />
                      ))}
                    </SimpleGrid>
                  )}
                </Card>
              ))}
              <Group justify="space-between">
                <Button variant="default" onClick={() => setStep(0)}>{t("Nazad")}</Button>
                <Button disabled={!pages.length || !!busy} onClick={() => setStep(2)} rightSection={<IconArrowRight size={16} />}>{t("Dalje: predaja")}</Button>
              </Group>
            </Stack>
          )}

          {step === 2 && (
            <Stack>
              <Text size="sm" c="dimmed">{t("Svaki prilog postaje jedan PDF dokument u DMS-u (djelovodni broj), vezan za predmet. Provjerite da su stranice čitljive i pravilno okrenute.")}</Text>
              {filled.map((g, i) => (
                <Paper key={g.no} withBorder p="sm" radius="md">
                  <Group justify="space-between">
                    <Text fw={600}>{t("Prilog")} {i + 1}: {titles[g.no] || t("bez naziva — naziv određuje referent")}</Text>
                    <Badge variant="light">{g.pages.length} {t("str.")}</Badge>
                  </Group>
                </Paper>
              ))}
              <Textarea label={t("Napomena uz dokumentaciju")} autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)}
                placeholder={t("npr. originali vraćeni podnosiocu, ovjerene kopije zadržane")} />
              <Group justify="space-between">
                <Button variant="default" onClick={() => setStep(1)}>{t("Nazad")}</Button>
                <Button color="green" leftSection={<IconCheck size={16} />} loading={busy === "submit"} onClick={submit}>{t("Predaj skup priloga")}</Button>
              </Group>
            </Stack>
          )}
          {zoom && <ZoomModal bid={batch.id} page={zoom} onClose={() => setZoom(null)} />}
        </Stack>
      )}
    </Modal>
  );
}

// ============================================================================== raspoređivanje (referent)

interface DocType { code: string; name: string; active: boolean }
interface HoldingOrchard { id: number; name: string; species: string; cultivar?: string | null }

function ClassifyForm({ doc, holdingId, onDone }: { doc: ScanDoc; holdingId: number | null; onDone: () => void }) {
  const { t } = useT();
  const types = useQuery({ queryKey: ["dms-doc-types"], queryFn: () => http.get<DocType[]>("/api/v1/dms/doc-types") });
  const orchards = useQuery({
    queryKey: ["producer-orchards", holdingId], enabled: !!holdingId,
    queryFn: () => http.get<{ orchards: HoldingOrchard[] }>(`/api/v1/producers/${holdingId}`),
  });
  const { ko } = useKoAndMunicipalities();
  const [v, setV] = useState({
    doc_type: doc.rasporedjen ? doc.doc_type : null as string | null,
    title: doc.rasporedjen ? doc.title : (doc.metadata.naziv_podnosioca ?? ""),
    doc_date: doc.doc_date ?? "", description: doc.description ?? "",
    orchard_ids: doc.orchard_ids.map(String), ko_id: doc.ko_id ? String(doc.ko_id) : null as string | null,
    parcels: doc.parcel_numbers.join(", "),
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const k = (ko ?? []).find((x) => String(x.id) === v.ko_id);
      await http.post(`/api/v1/scan/documents/${doc.id}/classify`, {
        doc_type: v.doc_type, title: v.title, doc_date: v.doc_date || null, description: v.description || null,
        orchard_ids: v.orchard_ids.map(Number), ko_id: k?.id ?? null, ko_name: k?.name ?? null,
        parcel_numbers: v.parcels.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean),
      });
      notifications.show({ color: "green", message: t("Prilog je raspoređen") });
      onDone();
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(false); }
  };
  return (
    <Stack gap="xs">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
        <Select label={t("Vrsta dokumenta")} required searchable value={v.doc_type} onChange={(x) => setV({ ...v, doc_type: x })}
          data={(types.data ?? []).filter((x) => x.active && x.code !== "skenirani_prilog").map((x) => ({ value: x.code, label: t(x.name) }))} />
        <TextInput label={t("Naziv dokumenta")} required value={v.title} onChange={(e) => setV({ ...v, title: e.currentTarget.value })} />
        <TextInput label={t("Datum dokumenta")} type="date" value={v.doc_date} onChange={(e) => setV({ ...v, doc_date: e.currentTarget.value })} />
        <MultiSelect label={t("Voćnjak (gazdinstvo iz predmeta)")} value={v.orchard_ids} onChange={(x) => setV({ ...v, orchard_ids: x })} clearable
          data={(orchards.data?.orchards ?? []).map((o) => ({ value: String(o.id), label: `${o.name} (${o.species}${o.cultivar ? `, ${o.cultivar}` : ""})` }))}
          placeholder={holdingId ? t("nije vezano za voćnjak") : t("gazdinstvo još nije u registru")} disabled={!holdingId} />
        <Select label={t("Katastarska opština")} searchable clearable value={v.ko_id} onChange={(x) => setV({ ...v, ko_id: x })}
          data={(ko ?? []).map((x) => ({ value: String(x.id), label: `${x.name}${x.opstina ? ` (${x.opstina})` : ""}` }))} />
        <TextInput label={t("Katastarske čestice")} placeholder="123/4, 125" value={v.parcels} onChange={(e) => setV({ ...v, parcels: e.currentTarget.value })} />
      </SimpleGrid>
      <Textarea label={t("Opis / napomena")} autosize minRows={1} value={v.description} onChange={(e) => setV({ ...v, description: e.currentTarget.value })} />
      <Group justify="flex-end">
        <Button size="xs" loading={busy} disabled={!v.doc_type || v.title.trim().length < 3} onClick={save} leftSection={<IconCheck size={14} />}>
          {t(doc.rasporedjen ? "Sačuvaj izmjene" : "Rasporedi")}</Button>
      </Group>
    </Stack>
  );
}

function SplitModal({ doc, onClose, onDone }: { doc: ScanDoc; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const n = doc.metadata.stranica ?? 1;
  const [cuts, setCuts] = useState("");
  const [busy, setBusy] = useState(false);
  const parts = useMemo(() => {
    const at = [...new Set(cuts.split(/[,;\s]+/).map(Number).filter((x) => x >= 1 && x < n))].sort((a, b) => a - b);
    const out: number[][] = [];
    let s = 1;
    for (const c of [...at, n]) { out.push(Array.from({ length: c - s + 1 }, (_, i) => s + i)); s = c + 1; }
    return out;
  }, [cuts, n]);
  const go = async () => {
    setBusy(true);
    try {
      await http.post(`/api/v1/scan/documents/${doc.id}/split`, { parts });
      notifications.show({ color: "green", message: `${t("Prilog je podijeljen na")} ${parts.length}` });
      onDone(); onClose();
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(false); }
  };
  return (
    <Modal opened onClose={onClose} title={`${t("Podjela priloga")} ${doc.reg_no}`}>
      <Stack>
        <Text size="sm">{t(`Prilog ima ${n} stranica. Upišite poslije koje stranice se dijeli (npr. „2, 4“).`)}</Text>
        <TextInput label={t("Podijeli poslije stranice")} value={cuts} onChange={(e) => setCuts(e.currentTarget.value)} placeholder="2" />
        <Text size="sm">{parts.map((p, i) => `${t("dokument")} ${i + 1}: ${p[0]}${p.length > 1 ? `–${p[p.length - 1]}` : ""}`).join(" · ")}</Text>
        <Group justify="flex-end"><Button disabled={parts.length < 2} loading={busy} onClick={go} leftSection={<IconScissors size={16} />}>{t("Podijeli")}</Button></Group>
      </Stack>
    </Modal>
  );
}

function DocPreview({ id }: { id: number }) {
  const url = useAuthBlobUrl(`/api/v1/scan/documents/${id}/file`);
  if (!url) return <Loader size="sm" />;
  return <iframe title="pdf" src={url} style={{ width: "100%", height: 420, border: "1px solid var(--mantine-color-default-border)", borderRadius: 8 }} />;
}

export function ScannedAttachments({ caseId, caseNumber, holdingId, canScan, canClassify }: {
  caseId: number; caseNumber?: string; holdingId: number | null; canScan: boolean; canClassify: boolean;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const [wizard, setWizard] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const [split, setSplit] = useState<ScanDoc | null>(null);
  const q = useQuery({ queryKey: ["scan-batches", caseId], queryFn: () => http.get<Batch[]>(`/api/v1/scan/cases/${caseId}`) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["scan-batches", caseId] }); qc.invalidateQueries({ queryKey: ["case", String(caseId)] }); };
  const batches = (q.data ?? []).filter((b) => b.status !== "u_toku" || (b.pages as number) > 0);
  const unassigned = batches.reduce((s, b) => s + (b.unassigned ?? 0), 0);
  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group gap="xs">
          <Text fw={700}>{t("Skenirana dokumentacija")}</Text>
          {unassigned > 0 && <Badge color="orange">{unassigned} {t("čeka raspoređivanje")}</Badge>}
        </Group>
        {canScan && <Button size="xs" leftSection={<IconScan size={14} />} onClick={() => setWizard(true)}>{t("Skeniraj dokumentaciju")}</Button>}
      </Group>
      {!batches.length && <Text size="sm" c="dimmed">{t("Nema skenirane dokumentacije.")}</Text>}
      {batches.map((b) => (
        <Paper key={b.id} withBorder radius="md" p="sm">
          <Group justify="space-between" mb={6}>
            <Group gap="xs">
              <Text fw={600} size="sm">{b.number}</Text>
              <Badge size="sm" color={BATCH_STATUS[b.status]?.color}>{t(BATCH_STATUS[b.status]?.label ?? b.status)}</Badge>
            </Group>
            <Text size="xs" c="dimmed">{b.created_by_name ?? b.created_by} · {fmtDateTime(b.submitted_at ?? b.created_at)}</Text>
          </Group>
          {b.note && <Text size="xs" c="dimmed" mb={6}>{b.note}</Text>}
          {b.status === "u_toku" && <Text size="sm" c="dimmed">{t("Skeniranje nije završeno (skup nije predat).")}</Text>}
          <Stack gap={6}>
            {b.documents.map((d) => (
              <Paper key={d.id} withBorder radius="sm" p="xs" bg={d.rasporedjen ? undefined : "var(--mantine-color-orange-light)"}>
                <Group justify="space-between" wrap="nowrap">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={600} truncate>{t("Prilog")} {d.metadata.prilog}: {d.title}</Text>
                    <Text size="xs" c="dimmed">{d.reg_no} · {d.metadata.stranica} {t("str.")} · {fmtSize(d.size_bytes)}
                      {d.rasporedjen ? ` · ${t(d.doc_type_name)}${d.doc_date ? ` · ${fmtDate(d.doc_date)}` : ""}${d.ko_name ? ` · KO ${d.ko_name}` : ""}${d.parcel_numbers.length ? ` · KČ ${d.parcel_numbers.join(", ")}` : ""} · ${t("rasporedio")} ${d.metadata.rasporedio}` : ` · ${t("čeka raspoređivanje")}`}</Text>
                  </div>
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label={t("Otvori PDF")}><ActionIcon variant="subtle" onClick={() => downloadFile(`/api/v1/scan/documents/${d.id}/file`, {}, true).catch((e) => notifications.show({ color: "red", message: errMsg(e) }))}>
                      <IconZoomIn size={16} /></ActionIcon></Tooltip>
                    {canClassify && !d.rasporedjen && (d.metadata.stranica ?? 1) > 1 && (
                      <Tooltip label={t("Podijeli na više dokumenata")}><ActionIcon variant="subtle" onClick={() => setSplit(d)}><IconScissors size={16} /></ActionIcon></Tooltip>
                    )}
                    {canClassify && (
                      <Button size="xs" variant={d.rasporedjen ? "subtle" : "filled"} color={d.rasporedjen ? "gray" : "orange"}
                        onClick={() => setOpen(open === d.id ? null : d.id)}>{t(d.rasporedjen ? "Izmijeni" : "Rasporedi")}</Button>
                    )}
                  </Group>
                </Group>
                {open === d.id && (
                  <>
                    <Divider my="xs" />
                    <SimpleGrid cols={{ base: 1, lg: 2 }}>
                      <DocPreview id={d.id} />
                      <ClassifyForm doc={d} holdingId={holdingId} onDone={() => { setOpen(null); refresh(); }} />
                    </SimpleGrid>
                  </>
                )}
              </Paper>
            ))}
          </Stack>
        </Paper>
      ))}
      {wizard && <ScanWizard caseId={caseId} caseNumber={caseNumber} onClose={() => { setWizard(false); refresh(); }} />}
      {split && <SplitModal doc={split} onClose={() => setSplit(null)} onDone={refresh} />}
      {!canClassify && unassigned > 0 && <Text size="xs" c="dimmed">{t("Referent će rasporediti priloge u toku obrade predmeta.")}</Text>}
    </Stack>
  );
}
